import React, { useContext } from "react";
import classnames from "classnames";

// Styles
import Styles from "./MarketplaceLoadCardV3.module.scss";

// Core Constants & Methods
import { AppContext } from "@vahak/core/dist/app-context";
import { getLorryDetailsByType, LORRY_CAPACITY_UNIT_TYPES, PAYMENT_TYPE } from "@vahak/core/dist/constants";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
import { formatDate, getPriceString, toValidDate } from "@vahak/core-ui/dist/methods";

// Icons
import LoadBoxIcon from "@vahak/core/dist/standard-icons/load-outlined.svg";
import ShareIcon from "@vahak/core/dist/icons/share_blue.svg";

// Local Components
import IfCanAccess from "../../admin/IfCanAccess/IfCanAccess";

// Local Types & Methods
import { LoadMarketCard } from "../../../services/load/useGetLoadMarketV2/type";
import useHomepageV2Translation from "../hooks/useHomepageV2Translation";
import { LoadMarketRouteSection } from "../load-market-route";
import { getLoadDisplayBodyType } from "../getLoadDisplayBodyType";

// =============================== * =============================== * =============================== //

interface MarketplaceLoadCardV3Props {
    id?: string;
    data: LoadMarketCard;
    onBidNowClick?: () => void;
    className?: string;
    hideShareButton?: boolean;
}

const MarketplaceLoadCardV3 = ({ id, data, onBidNowClick, className, hideShareButton }: MarketplaceLoadCardV3Props) => {
    const { t } = useHomepageV2Translation();
    const { setShowShareModalPopup, companyId } = useContext(AppContext);
    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const { load_detail: loadDetail, company_detail: companyDetail } = data;
    const cardId = id || `mkt-load-card-${loadDetail?.id || "preview"}`;
    const lorryInfo = getLorryDetailsByType(loadDetail?.lorry_type_id);
    const capacityUnit =
        lorryInfo?.unit === LORRY_CAPACITY_UNIT_TYPES.TONNES
            ? loadDetail?.quantity === 1
                ? t("mktCardV2:tonne")
                : t("mktCardV2:tonnes")
            : lorryInfo?.unitSingular || t("mktCardV2:tonne");
    const sizeValue = loadDetail?.size;
    const sizeUnit = loadDetail?.size_text || t("mktCardV2:ft");
    const bodyType = getLoadDisplayBodyType(loadDetail?.lorry_type_id, loadDetail?.body_type);
    const lorryTypeLabel = lorryInfo?.label;
    const loadingTime = loadDetail?.loading_timestamp
        ? formatDate(toValidDate(loadDetail.loading_timestamp * 1000), "DD MMM YYYY, h:mm a")
        : "--";
    // const isTopUser = !!companyDetail?.is_priority_user;
    // const isTopRoute = !!loadDetail?.meta_data?.is_load_in_top_routes;
    const isBestPrice = !!loadDetail?.meta_data?.is_best_price;

    const subtitleParts: string[] = [];
    if (loadDetail?.quantity) {
        subtitleParts.push(`${loadDetail.quantity} ${capacityUnit}`);
    }
    if (loadDetail?.total_tyres) {
        const tyreLabel = loadDetail.total_tyres > 1 ? t("mktCardV2:tyres") : t("mktCardV2:tyre");
        subtitleParts.push(`${loadDetail.total_tyres} ${tyreLabel}`);
    }
    if (sizeValue) {
        subtitleParts.push(`${sizeValue} ${sizeUnit}`);
    }

    const lorryTypeDisplay = bodyType ? `${lorryTypeLabel} (${bodyType})` : lorryTypeLabel || "--";

    const subtitleText = subtitleParts.join(" • ");

    const paymentTypeLabel =
        loadDetail?.payment_type === PAYMENT_TYPE.Advance ? t("mktCardV2:paymentAdvance") : t("mktCardV2:paymentToPay");

    const advancePercentage = loadDetail?.advance_payment_percentage;
    const paymentMeta =
        loadDetail?.payment_type === PAYMENT_TYPE.Advance
            ? advancePercentage
                ? `${advancePercentage}% ${paymentTypeLabel}`
                : paymentTypeLabel
            : paymentTypeLabel;

    const showPriceSubtext = !!paymentMeta;

    const toggleShareModal = () => {
        setShowShareModalPopup?.({
            isVisible: true,
            mktType: "load",
            data: {
                id: loadDetail.id,
                source: loadDetail.source_city_name,
                destination: loadDetail.destination?.split("(")[0],
                quantity: loadDetail.quantity,
                lorry_type_id: getLorryDetailsByType(loadDetail?.lorry_type_id)?.id,
                lorry_type_text: loadDetail.lorry_type,
                transporter_name: loadDetail.destination_city_name,
                oppositeCompanyId: companyId,
                loadCardData: data
            }
        });
    };

    const handleStopsClick = () => {
        sendGAandMoEngageEvent({
            name: GA4EventNames.WEB_V2.stop_clicked,
            data: { source_screen: WEB_V2_SOURCE_SCREENS.BID_FORM }
        });
    };

    return (
        <div className={classnames(Styles.card, className)} id={cardId}>
            <div className={Styles.header}>
                <div className={Styles.iconWrapper}>
                    <LoadBoxIcon />
                </div>

                <div className={Styles.headerContent}>
                    <p className={Styles.title} id={`${cardId}--quantity`} title={subtitleText}>
                        {subtitleText || t("mktCardV2:goods")}
                    </p>
                    {loadDetail?.material_type ? (
                        <p className={Styles.subtitle} id={`${cardId}--material`} title={loadDetail?.material_type}>
                            {loadDetail?.material_type}
                        </p>
                    ) : null}
                </div>

                {/* {(isTopUser || isTopRoute) && (
                    <div className={Styles.badges}>
                        {isTopUser && (
                            <span className={classnames(Styles.badge, Styles.topUserBadge)}>
                                <StarIcon className={Styles.badgeIcon} aria-hidden />
                                {t("mktCardV2:topUser")}
                            </span>
                        )}
                        {isTopRoute && (
                            <span className={classnames(Styles.badge, Styles.topRouteBadge)}>
                                <TopRouteBadgeIcon className={Styles.badgeIcon} aria-hidden />
                                {t("mktCardV2:topRoute")}
                            </span>
                        )}
                    </div>
                )} */}

                {!hideShareButton ? (
                    <button
                        type="button"
                        className={Styles.shareBtn}
                        onClick={toggleShareModal}
                        id={`${cardId}--share`}
                        aria-label={t("common:share_load")}
                    >
                        <ShareIcon aria-hidden />
                    </button>
                ) : null}
            </div>

            <LoadMarketRouteSection
                loadDetail={loadDetail}
                variant="marketCard"
                idPrefix={id}
                onStopsClick={handleStopsClick}
            />

            <div className={Styles.detailsSection}>
                <div className={Styles.detailBlock}>
                    <span className={Styles.detailLabel}>{t("mktCardV2:lorryType")}</span>
                    <p className={Styles.detailValue} id={`${cardId}--lorryType`}>
                        {lorryTypeDisplay}
                    </p>
                </div>

                <div className={Styles.detailBlock}>
                    <span className={Styles.detailLabel}>{t("mktCardV2:loadingTime")}</span>
                    <p className={Styles.detailValue} id={`${cardId}--loadingTime`}>
                        {loadingTime}
                    </p>
                </div>
            </div>

            <div className={Styles.footer}>
                <div className={Styles.priceSection}>
                    <span className={Styles.priceLabel}>{t("mktCardV2:price")}</span>
                    <div className={Styles.priceRow}>
                        <span className={Styles.priceValue} id={`${cardId}--expectedPrice`}>
                            {loadDetail?.expected_amount ? getPriceString(loadDetail.expected_amount) : "--"}
                        </span>
                        {isBestPrice && <span className={Styles.bestPriceBadge}>{t("mktCardV2:bestPrice")}</span>}
                    </div>
                    {showPriceSubtext ? (
                        <p className={Styles.priceSubtext} id={`${cardId}--priceMeta`}>
                            <span className={Styles.priceSubtextAdvance}>({paymentMeta})</span>
                        </p>
                    ) : null}
                </div>

                {!!onBidNowClick && (
                    <IfCanAccess action={""} allowCustomer>
                        <button
                            type="button"
                            className={Styles.bidNowBtn}
                            onClick={onBidNowClick}
                            id={`${cardId}--bidNow`}
                        >
                            {t("mktCardV2:bidNow")}
                        </button>
                    </IfCanAccess>
                )}
            </div>
        </div>
    );
};

export default MarketplaceLoadCardV3;
